import type { AssistantSseEvent, AssistantTone } from '@contracts/assistant';

/** Hana's fourteen faces, the persona mockup's `E` keys. */
export const EMOTIONS = ['idle', 'greet', 'listen', 'think', 'work', 'ask', 'proud', 'surprise', 'worried',
  'confused', 'sorry', 'sleepy', 'wink', 'love'] as const;
export type Emotion = (typeof EMOTIONS)[number];

/** How long proud, greet and a tone's emotion stay on before Hana goes back to idle. */
export const HOLD_MS = 4000;
/** D91: this long without any activity and Hana dozes off. */
export const SLEEPY_MS = 600_000;

/** D90: the model's tone label adds a face; neutral adds none. */
export const TONE_EMOTION: Record<AssistantTone, Emotion | null> = {
  happy: 'greet', proud: 'proud', worried: 'worried', surprised: 'surprise', confused: 'confused',
  playful: 'wink', grateful: 'love', neutral: null,
};
/** D122: a tool refused for these makes Hana sorry; NOT_FOUND does not (hidden = missing). */
export const PERMISSION_CODES: readonly string[] = ['FORBIDDEN', 'ASSISTANT_READ_ONLY', 'ASSISTANT_DISABLED'];

export type EmotionState = {
  sorry: boolean; asking: boolean; working: readonly string[]; thinking: boolean; typing: boolean;
  /** S5 T2.3: the batch cards on screen whose job is still reading; Hana thinks while any is. */
  reading: readonly string[];
  /** Calls the person confirmed on a card; an ok `tool_done` of one is a write (D122). */
  confirmed: readonly string[];
  wrote: boolean; proudUntil: number; greetUntil: number;
  tone: { emotion: Emotion; until: number } | null;
  lastActivity: number;
};
export type EmotionInput =
  | { kind: 'opened' } | { kind: 'typing'; active: boolean } | { kind: 'interaction' }
  | { kind: 'event'; event: AssistantSseEvent } | { kind: 'pending'; open: boolean }
  | { kind: 'confirmed'; callId: string } | { kind: 'refused' }
  | { kind: 'reading'; jobId: string; active: boolean };

export const initialEmotion = (now: number): EmotionState => ({ sorry: false, asking: false, working: [],
  reading: [], thinking: false, typing: false, confirmed: [], wrote: false, proudUntil: 0, greetUntil: 0, tone: null,
  lastActivity: now });

/** Spec §7.2: system events decide; the model only adds a tone (D90). Every input is activity (D91). */
export function reduceEmotion(s0: EmotionState, input: EmotionInput, now: number): EmotionState {
  const s = { ...s0, lastActivity: now };
  switch (input.kind) {
    case 'opened': return { ...s, greetUntil: now + HOLD_MS };
    case 'typing': return { ...s, typing: input.active, sorry: input.active ? false : s.sorry };
    case 'interaction': return s;
    case 'pending': return { ...s, asking: input.open };
    case 'confirmed': return { ...s, sorry: false, confirmed: [...s.confirmed, input.callId] };
    case 'refused': return { ...s, sorry: true };
    case 'reading': {
      const others = s.reading.filter((j) => j !== input.jobId);
      return { ...s, reading: input.active ? [...others, input.jobId] : others };
    }
    case 'event': break;
  }
  const e = input.event;
  switch (e.type) {
    case 'turn_started': return { ...s, sorry: false, thinking: false, working: [], typing: false, wrote: false };
    case 'thinking': return { ...s, thinking: true };
    case 'tool_started': return { ...s, thinking: false,
      working: s.working.includes(e.data.callId) ? s.working : [...s.working, e.data.callId] };
    case 'tool_done': return { ...s, working: s.working.filter((c) => c !== e.data.callId),
      sorry: s.sorry || (!e.data.ok && PERMISSION_CODES.includes(e.data.errorCode ?? '')),
      wrote: s.wrote || (e.data.ok && s.confirmed.includes(e.data.callId)) };
    case 'confirm_required': return { ...s, asking: true, thinking: false };
    case 'message_delta': return { ...s, thinking: false };
    case 'message_done': {
      const emotion = e.data.tone === null ? null : TONE_EMOTION[e.data.tone];
      return { ...s, thinking: false, tone: emotion === null ? s.tone : { emotion, until: now + HOLD_MS } };
    }
    case 'undo_available': return { ...s, wrote: true };
    case 'error': return { ...s, sorry: true, thinking: false, working: [] };
    case 'turn_done': return { ...s, thinking: false, working: [], wrote: false, confirmed: [],
      proudUntil: s.wrote ? now + HOLD_MS : s.proudUntil };
  }
}

/** The priority order of spec §7.2: sorry > ask > work > think > listen > proud > greet > tone > sleepy > idle. */
export function emotionOf(s: EmotionState, now: number): Emotion {
  if (s.sorry) return 'sorry';
  if (s.asking) return 'ask';
  if (s.working.length > 0) return 'work';
  if (s.thinking || s.reading.length > 0) return 'think';
  if (s.typing) return 'listen';
  if (s.proudUntil > now) return 'proud';
  if (s.greetUntil > now) return 'greet';
  if (s.tone !== null && s.tone.until > now) return s.tone.emotion;
  if (now - s.lastActivity >= SLEEPY_MS) return 'sleepy';
  return 'idle';
}

/** When `emotionOf` changes next without any input (a hold ends, sleepy begins); null = never. */
export function nextChangeAt(s: EmotionState, now: number): number | null {
  const times = [s.proudUntil, s.greetUntil, s.tone?.until ?? 0, s.lastActivity + SLEEPY_MS].filter((t) => t > now);
  return times.length === 0 ? null : Math.min(...times);
}
